import { isModelKind, modelPosesAt, replayModels } from './models.js';
import { isPosterKind, posterFacing } from './posters.js';
/**
 * The scene at a step: where every object is, which way it is turned, how big
 * it is and whether it is there, once the lesson's steps up to that one have
 * shown, hidden and moved things (docs/superpowers/specs/2026-09-28-wow-tools-design.md).
 *
 * Pure and shared: the headset renders it, the validator and the generator's
 * checks judge against it, and framing (bounds.ts, framing.ts) measures it, so
 * all of them agree on what is where at every step. A session sends a step,
 * not a history (ADR-007), so a headset that joins late, a teacher who jumps
 * and a pupil who goes back all get the right scene by computing it.
 *
 * It reads defensively: the generator's checks call it on answers that may
 * not have passed the schema, and anything it cannot read is left as it was.
 */
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const isVec3 = (value) => Array.isArray(value) &&
    value.length === 3 &&
    value.every((n) => typeof n === 'number' && Number.isFinite(n));
const isScale = (value) => typeof value === 'number' && Number.isFinite(value) && value > 0;
/** The ids in a step's `show` or `hide`, whatever else the list holds. */
export function idsIn(list) {
    return Array.isArray(list) ? list.filter((id) => typeof id === 'string') : [];
}
/** A step's moves that name an object, whatever else the list holds. */
export function movesIn(list) {
    return Array.isArray(list)
        ? list.filter((move) => isRecord(move) && typeof move['object'] === 'string')
        : [];
}
/**
 * Every object's state once steps 0 to `index` have happened: its declared
 * state (`hidden`, `pos`, `rot`, `scale`), then each step's `hide`, `show`
 * and `move`, in that order. Before the first step (`index` < 0) it is the
 * declared state; past the last, the last step's.
 *
 * An orbiting object's `pos` is the centre of its orbit, its parent's `pos`
 * at that step, and it is there only while its parent is.
 *
 * A library model (ADR-043) is where the steps' `models` walked it and turned
 * the way they left it (`rot` [0, turn, 0]): models.ts's replay, the calm
 * path. A `face: "pupil"` turns it to `options.pupil` -- the step's framed
 * eyes, which framing.ts's `modelStatesAt` passes -- or to the origin.
 */
export function sceneStateAt(lesson, index, options = {}) {
    const doc = lesson;
    const objects = isRecord(doc['objects']) ? doc['objects'] : {};
    const own = new Map();
    for (const [id, object] of Object.entries(objects)) {
        if (!isRecord(object))
            continue;
        // A poster is turned by its facing, never by `rot` (posters.ts); a
        // library model by its `turn` (ADR-043, plan C1).
        const kind = typeof object['kind'] === 'string' ? object['kind'] : '';
        const turn = object['turn'];
        own.set(id, {
            visible: object['hidden'] !== true,
            pos: isVec3(object['pos']) ? object['pos'] : undefined,
            rot: isPosterKind(kind)
                ? [0, posterFacing(lesson, id), 0]
                : isModelKind(kind)
                    ? [0, typeof turn === 'number' && Number.isFinite(turn) ? turn : 0, 0]
                    : isVec3(object['rot'])
                        ? object['rot']
                        : [0, 0, 0],
            scale: isScale(object['scale']) ? object['scale'] : 1,
        });
    }
    const steps = Array.isArray(doc['steps']) ? doc['steps'] : [];
    const last = Math.min(index, steps.length - 1);
    for (let i = 0; i <= last; i++) {
        const step = steps[i];
        if (!isRecord(step))
            continue;
        for (const id of idsIn(step['hide'])) {
            const state = own.get(id);
            if (state)
                state.visible = false;
        }
        for (const id of idsIn(step['show'])) {
            const state = own.get(id);
            if (state)
                state.visible = true;
        }
        for (const move of movesIn(step['move'])) {
            const state = own.get(move.object);
            if (!state)
                continue;
            if (isVec3(move['pos']))
                state.pos = move['pos'];
            if (isVec3(move['rot']))
                state.rot = move['rot'];
            if (isScale(move['scale']))
                state.scale = move['scale'];
        }
    }
    const models = modelPosesAt(replayModels(lesson, { ...options, until: index }), index);
    for (const [id, pose] of models) {
        const state = own.get(id);
        if (!state)
            continue;
        state.pos = pose.pos;
        state.rot = [0, pose.turn, 0];
    }
    const parentOf = (id) => {
        const object = objects[id];
        const parent = isRecord(object) ? object['parent'] : undefined;
        return typeof parent === 'string' && parent !== id && own.has(parent) ? parent : undefined;
    };
    const states = new Map();
    for (const [id, state] of own) {
        // Up the chain of parents: a moon of a hidden planet is not there either,
        // and one without a `pos` of its own is where its parent is.
        let visible = state.visible;
        let pos = state.pos;
        const seen = new Set([id]);
        for (let at = parentOf(id); at !== undefined && !seen.has(at); at = parentOf(at)) {
            seen.add(at);
            const parent = own.get(at);
            visible &&= parent.visible;
            pos ??= parent.pos;
        }
        states.set(id, { visible, pos: pos ?? [0, 0, 0], rot: state.rot, scale: state.scale });
    }
    return states;
}
//# sourceMappingURL=scene-state.js.map